import { Box, Dialog, DialogContent, Skeleton, Stack, Typography } from '@mui/material';
import { FiCalendar } from 'react-icons/fi';
import Button from 'components/Common/Button';
import { useGetA2pCampaignPricing } from '@/apis/business/setting/phoneNumbers/hook';
import { formatDollars } from '@/utils/helperFunctions';

type Props = {
  open: boolean;
  usecase: string;
  isResubmit: boolean;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

const BORDER_COLOR = '#E5E7EB';
const pillSx = { textTransform: 'none', fontWeight: 500, borderRadius: '999px', px: 2.5 } as const;
const outlinedPillSx = {
  ...pillSx,
  borderColor: BORDER_COLOR,
  color: 'text.primary',
  bgcolor: '#fff',
  '&:hover': { borderColor: BORDER_COLOR, bgcolor: '#F9FAFB' }
} as const;

function Row({ label, amount, badge }: { label: string; amount: string; badge?: boolean }) {
  return (
    <Box display='flex' justifyContent='space-between' alignItems='center' py={0.75}>
      <Typography fontSize={13} color='text.secondary' display='flex' alignItems='center' gap={0.75}>
        {label}
        {badge && (
          <Box
            component='span'
            display='inline-flex'
            alignItems='center'
            gap={0.5}
            px={0.75}
            py={0.25}
            borderRadius='4px'
            bgcolor='#EEF0F3'
            fontSize={10}
            color='text.secondary'
          >
            <FiCalendar size={10} />
            Due Today
          </Box>
        )}
      </Typography>
      <Typography fontSize={13} fontWeight={600} color='text.primary'>
        {amount}
      </Typography>
    </Box>
  );
}

export default function CampaignPaymentConfirmDialog({
  open,
  usecase,
  isResubmit,
  isPending,
  onCancel,
  onConfirm
}: Props) {
  const { data, isLoading, isError } = useGetA2pCampaignPricing(open);
  const pricing = data?.data;
  const vettingFee = pricing?.vetting_fee ?? 0;
  const monthly =
    usecase === 'LOW_VOLUME' ? (pricing?.low_volume_campaign_per_month ?? 0) : (pricing?.mixed_campaign_per_month ?? 0);

  return (
    <Dialog
      open={open}
      onClose={isPending ? undefined : onCancel}
      PaperProps={{ sx: { borderRadius: '4px', width: 340, maxWidth: 'calc(100% - 32px)', m: 2 } }}
    >
      <DialogContent sx={{ textAlign: 'center', px: 2, pt: 3, pb: 3 }}>
        <Typography fontSize={15} fontWeight={600} color='text.primary' mb={0.75}>
          Confirm Payment
        </Typography>
        <Typography fontSize={14} color='text.primary' mb={2}>
          {isResubmit
            ? 'Resubmitting this campaign is charged a new vetting fee. Do you want to proceed with this payment?'
            : 'Are you sure you want to proceed with this payment?'}
        </Typography>

        <Box
          bgcolor='#F9FAFB'
          border={`1px solid ${BORDER_COLOR}`}
          borderRadius='4px'
          px={1.5}
          py={0.75}
          textAlign='left'
        >
          {isLoading ? (
            <Skeleton height={90} />
          ) : isError ? (
            <Typography fontSize={13} color='error' py={1}>
              Unable to load campaign pricing. Please try again.
            </Typography>
          ) : (
            <>
              <Row label='Vetting fee' amount={formatDollars(vettingFee)} badge />
              <Row label='Monthly campaign cost' amount={formatDollars(monthly)} />
              <Box
                display='flex'
                justifyContent='space-between'
                alignItems='center'
                borderTop={`1px solid ${BORDER_COLOR}`}
                mt={0.75}
                pt={1.25}
                pb={0.5}
              >
                <Typography fontSize={13} fontWeight={600} color='text.primary'>
                  Due Today
                </Typography>
                <Typography fontSize={13} fontWeight={600} color='primary.main'>
                  {formatDollars(vettingFee)}
                </Typography>
              </Box>
            </>
          )}
        </Box>
        <Typography fontSize={12} color='text.secondary' mt={1.25} mb={2.5} textAlign='left'>
          The monthly campaign cost is billed only once the campaign is approved.
        </Typography>

        <Stack direction='row' spacing={1.5} justifyContent='center'>
          <Button variant='outlined' onClick={onCancel} disabled={isPending} sx={{ ...outlinedPillSx, minWidth: 100 }}>
            Cancel
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={onConfirm}
            isPending={isPending}
            disabled={isLoading || isError}
            sx={{ ...pillSx, minWidth: 80 }}
          >
            Yes Sure
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
